import React, { useEffect, useState } from 'react';
import { Card, Form, Table, Button, Row, Col, Modal } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import SearchSelect from './SearchSelect';
import { useClientRepricing } from '../utils/clientPrices';
import { daysFromTodayDR } from '../utils/dates';
import { buyerIdProblem } from '../utils/buyerId';
import { issuedInternal } from '../utils/invoiceEdit';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

const TIPO_OPTIONS = [
  { value: 'E31', label: 'E31 — Crédito Fiscal' },
  { value: 'E32', label: 'E32 — Consumidor Final' },
  { value: 'E44', label: 'E44 — Régimen Especial' },
  { value: 'E45', label: 'E45 — Gubernamental' },
];

const emptyItem = () => ({
  product: '', description: '', quantity: 1, unitPrice: 0, itbisRate: 0.18,
});

const defaultDueDate = () => daysFromTodayDR(30); // Dominican days (toISOString is UTC: tomorrow after 8 p.m.)

const suggestTipo = (client) => {
  if (!client) return 'E32';
  if (client.esGubernamental) return 'E45';
  if (client.regimenEspecial) return 'E44';
  if (client.rnc) return 'E31';
  return 'E32';
};

const InvoiceBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientId, setClientId] = useState('');
  const [tipoEcf, setTipoEcf] = useState('E32');
  const [dueDate, setDueDate] = useState(defaultDueDate());
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [invoiceExento, setInvoiceExento] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishedId, setPublishedId] = useState(null);
  const { user } = useAuth() || {};
  const isAdmin = user?.role === 'admin';
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingEcf, setSendingEcf] = useState(false);
  const [sendEcfError, setSendEcfError] = useState('');
  // "Facturas E32 internas" (Administración → Rangos e-NCF): a consumidor final factura is kept as an internal
  // comprobante when published. `savedInternal` = the one just saved came back that way.
  const [internalE32, setInternalE32] = useState(false);
  const [savedInternal, setSavedInternal] = useState(false);
  // Conduces → Sin facturar → "Facturar" opens this with ?conduce=: its lines (at its prices, or the client's / list price
  // when it was made without them) and its client are loaded for review, and the factura bills that conduce.
  const [searchParams] = useSearchParams();
  const fromConduce = id ? null : searchParams.get('conduce');
  const [conduceNote, setConduceNote] = useState('');
  // "Renovar alquiler" (Facturas, Alquileres) opens this with ?rental=: the rental's next factura, filled in (client,
  // comprobante, lines, the period in the notes). Nothing exists until it is saved; saving claims that period.
  const fromRental = id ? null : searchParams.get('rental');
  const [rentalDraft, setRentalDraft] = useState(null);
  // Editing an issued internal factura (admins, Protocolo 514): it stays issued and E32 — saving only corrects it.
  const [issued, setIssued] = useState(null); // { number } when it is one

  useEffect(() => {
    API.get('/clients').then(res => setClients(res.data)).catch(err => console.error(err));
    API.get('/products').then(res => setProducts(res.data)).catch(err => console.error(err));
    API.get('/invoices/internal-e32').then(res => setInternalE32(!!res.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    API.get(`/invoices/${id}`).then(({ data }) => {
      setIssued(issuedInternal(data) ? { number: data.number } : null);
      setClientId(data.client?._id || '');
      setTipoEcf(data.tipoEcf);
      setDueDate(data.dueDate ? data.dueDate.slice(0, 10) : defaultDueDate());
      setNotes(data.notes || '');
      // The whole invoice was exempt (every line): that's the switch. Otherwise each line keeps its own saved exemption.
      const allExempt = !!data.items?.length && data.items.every(it => it.itbisExento);
      setInvoiceExento(allExempt);
      setItems(data.items?.length ? data.items.map(it => ({
        product: it.product || '',
        itbisExento: allExempt ? false : !!it.itbisExento,
        sku: it.sku,
        description: it.description,
        quantity: it.quantity,
        unitPrice: it.unitPrice,
        itbisRate: it.itbisRate,
        unidadMedida: it.unidadMedida,
      })) : [emptyItem()]);
    }).catch(err => {
      console.error('Error loading invoice:', err);
      setError('No se pudo cargar la factura.');
    });
  }, [id]);

  useEffect(() => {
    if (!fromConduce || !products.length || !clients.length) return;
    API.get(`/conduces/${fromConduce}`).then(({ data: c }) => {
      const cid = c.client?._id || c.client;
      setClientId(cid);
      setTipoEcf(suggestTipo(clients.find(x => x._id === cid)));
      const byId = new Map(products.map(p => [p._id, p]));
      setItems((c.items || []).map(it => {
        const product = it.product ? byId.get(String(it.product?._id || it.product)) : null;
        return {
          product: product?._id || '',
          sku: it.sku || product?.sku,
          description: it.description,
          quantity: it.quantity,
          unitPrice: c.showPrices ? it.unitPrice : (product?.price || 0),
          itbisRate: it.itbisRate ?? product?.itbisRate ?? 0.18,
          itbisExento: c.showPrices ? !!it.itbisExento : !!product?.itbisExento,
          unidadMedida: it.unidadMedida ?? product?.unidadMedida,
        };
      }));
      if (!c.showPrices) repriceFor(cid, c.client?.name); // the client's special prices over the list price
      setConduceNote(`Factura del conduce ${c.number}${c.client?.name ? ` de ${c.client.name}` : ''}. Revisa los precios: ${c.showPrices ? 'son los del conduce' : 'el conduce no tenía precios; se usan los del cliente o de lista'}.`);
    }).catch(() => setError('No se pudo cargar el conduce.'));
  }, [fromConduce, products.length, clients.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fromRental) return;
    API.get(`/rentals/${fromRental}/invoice-draft`).then(({ data: d }) => {
      setRentalDraft(d);
      setClientId(d.client?._id || '');
      setTipoEcf(d.tipoEcf);
      setNotes(d.notes || '');
      setInvoiceExento(false);
      setItems(d.items.map(it => ({
        product: it.product || '', sku: it.sku, description: it.description, quantity: it.quantity, unitPrice: it.unitPrice,
        itbisRate: it.itbisRate ?? 0.18, itbisExento: !!it.itbisExento, unidadMedida: it.unidadMedida,
      })));
    }).catch(err => setError(err.response?.data?.message || 'No se pudo cargar el alquiler.'));
  }, [fromRental]);

  const selectedClient = clients.find(c => c._id === clientId);

  const { priceNote, repriceFor } = useClientRepricing(items, setItems);

  const handleClientChange = (id) => {
    setClientId(id);
    const client = clients.find(c => c._id === id);
    setTipoEcf(suggestTipo(client));
    repriceFor(id, client?.name); // lines added before the client was chosen get this client's special prices
  };

  const updateItem = (index, patch) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, ...patch } : it));
  };

  const handleProductChange = async (index, productId) => {
    const product = products.find(p => p._id === productId);
    if (!product) {
      updateItem(index, { product: '' });
      return;
    }

    let unitPrice = product.price;
    if (clientId) {
      try {
        const { data } = await API.get('/client-prices/resolve', { params: { client: clientId, product: productId } });
        unitPrice = data.price;
      } catch (err) {
        console.error('Error resolving client price:', err);
      }
    }

    updateItem(index, {
      product: productId,
      sku: product.sku,
      description: product.name,
      unitPrice,
      priceEdited: false,
      itbisRate: product.itbisRate,
      unidadMedida: product.unidadMedida,
    });
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  // A line carries no ITBIS when the whole invoice is exempt, when the line itself was saved exempt, or when its product is
  // exempt (e.g. oxígeno medicinal). Only a taxed product on a non-exempt invoice pays ITBIS.
  const productExento = (item) => !!products.find(p => p._id === item.product)?.itbisExento;
  const lineExento = (item) => invoiceExento || !!item.itbisExento || productExento(item);

  const totals = items.reduce((acc, item) => {
    const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
    acc.subtotal += lineTotal;
    if (!lineExento(item)) acc.itbis += lineTotal * (item.itbisRate ?? 0.18);
    return acc;
  }, { subtotal: 0, itbis: 0 });
  const grandTotal = totals.subtotal + totals.itbis;
  // DGII's buyer-id rules (utils/buyerId.js): shown under the type, and publishing is blocked until it's fixed.
  const idProblem = buyerIdProblem(tipoEcf, selectedClient, grandTotal);

  const handleSubmit = async () => {
    setError('');

    if (!clientId) {
      setError('Selecciona un cliente.');
      return;
    }
    if (idProblem) {
      setError(idProblem);
      return;
    }
    const validItems = items.filter(it => it.description && Number(it.quantity) > 0);
    if (validItems.length === 0) {
      setError('Agrega al menos una línea con descripción y cantidad.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client: clientId,
        tipoEcf,
        dueDate,
        notes,
        status: 'ready',
        ...(fromConduce && !id ? { conduceIds: [fromConduce] } : {}),
        ...(rentalDraft && !id ? { rentalId: rentalDraft.rental._id, rentalPeriod: rentalDraft.period.from } : {}),
        items: validItems.map(it => ({
          product: it.product || undefined,
          sku: it.sku,
          description: it.description,
          quantity: Number(it.quantity),
          unitPrice: Number(it.unitPrice),
          itbisExento: lineExento(it),
          itbisRate: it.itbisRate,
          unidadMedida: it.unidadMedida,
        })),
      };
      let savedId = id;
      if (id && issued) {
        await API.put(`/invoices/${id}`, payload);
        navigate('/invoices', { state: { notice: `Factura ${issued.number} corregida.` } });
        return;
      }
      if (id) {
        const { data } = await API.put(`/invoices/${id}`, payload);
        setSavedInternal(!!data.internal);
      } else {
        const { data } = await API.post('/invoices', payload);
        savedId = data._id;
        setSavedInternal(!!data.internal);
      }
      setPublishedId(savedId);
      setShowSendModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar la factura.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSend = async () => {
    setSendingEcf(true);
    setSendEcfError('');
    try {
      const { data } = await API.post(`/invoices/${publishedId}/send-ecf`);
      // A sales user's request is done here: the invoice waits for an admin on the Facturas page.
      navigate('/invoices', data.approvalRequested ? { state: { notice: `Pediste aprobación para enviar la factura ${data.invoice.number} a DGII. Un administrador la revisará y la enviará: no tienes que esperar. Mientras tanto aparece como "Esperando aprobación".${data.emailed > 0 ? '' : ' Los administradores la verán en esta lista.'}` } } : undefined);
    } catch (err) {
      setSendEcfError(err.response?.data?.message || 'Error al enviar la factura a DGII.');
      setSendingEcf(false);
    }
  };

  const handleSkipSend = () => {
    navigate('/invoices');
  };

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-4" style={{ textAlign: 'left' }}>{id ? 'Editar Factura' : 'Nueva Factura'}</h2>

      {error && <div className="alert alert-danger">{error}</div>}
      {issued && (
        <div className="alert alert-info">
          La factura <strong>{issued.number}</strong> ya está emitida: al guardar se corrige (cliente, líneas, vencimiento, notas)
          y sigue emitida, con su mismo número y su tipo de comprobante. El inventario se ajusta a las líneas nuevas.
        </div>
      )}
      {conduceNote && <div className="alert alert-info">{conduceNote}</div>}
      {rentalDraft && (
        <div className="alert alert-info">
          {rentalDraft.first ? 'Primera factura' : 'Renovación'} del alquiler <strong>{rentalDraft.rental.number}</strong>: período del{' '}
          {rentalDraft.period.from.split('-').reverse().join('/')} al {rentalDraft.period.to.split('-').reverse().join('/')}.
          Revisa y guarda para generar la factura; el alquiler pasa al período siguiente al guardarla.
        </div>
      )}

      <Card className="shadow-sm p-3 mb-3">
        <Row>
          <Col md={5}>
            <Form.Label>Cliente</Form.Label>
            <SearchSelect
              options={clients}
              value={clientId}
              getKey={c => c._id}
              getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
              onSelect={c => handleClientChange(c._id)}
              placeholder="Buscar cliente..."
              emptyLabel="Sin clientes coincidentes"
            />
          </Col>
          <Col md={4}>
            <Form.Label>Tipo de Comprobante</Form.Label>
            <Form.Select value={tipoEcf} disabled={!!issued} onChange={e => setTipoEcf(e.target.value)}>
              {TIPO_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Form.Select>
            {idProblem && (
              <div className="small mt-1" style={{ color: 'var(--mm-red)' }}>
                <i className="bi bi-exclamation-triangle me-1" />{idProblem}
              </div>
            )}
            {internalE32 && tipoEcf === 'E32' && !issued && (
              <div className="small mt-1" style={{ color: 'var(--mm-cyan)' }}>
                Se guardará como <strong>comprobante interno</strong>: no se envía a DGII ni usa e-NCF. Podrás imprimirla y, si hace falta, enviarla a DGII después.
              </div>
            )}
          </Col>
          <Col md={3}>
            <Form.Label>Vencimiento</Form.Label>
            <Form.Control type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </Col>
        </Row>
        {selectedClient && (
          <div className="text-muted small mt-2">
            {selectedClient.address || 'Sin dirección registrada'} · {selectedClient.email || 'Sin email'} · {selectedClient.phone || 'Sin teléfono'}
          </div>
        )}
        <Row className="mt-3">
          <Col md={9}>
            <Form.Label>Notas / comentario</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Visible en la factura impresa" />
          </Col>
        </Row>
      </Card>

      <Card className="shadow-sm p-3 mb-3">
        <Card.Title>Productos y Servicios</Card.Title>
        {priceNote && <div className="small mb-2" style={{ color: 'var(--mm-cyan)' }}>{priceNote}</div>}
        <Table responsive className="mb-2">
          <thead>
            <tr>
              <th style={{ width: 50 }}>No.</th>
              <th style={{ minWidth: 140 }}>No. de Item</th>
              <th style={{ minWidth: 180 }}>Producto</th>
              <th style={{ minWidth: 180 }}>Descripción</th>
              <th style={{ width: 90 }}>Cantidad</th>
              <th style={{ width: 130 }}>Precio Unit.</th>
              <th style={{ width: 110 }} className="text-end">Subtotal</th>
              <th style={{ width: 110 }} className="text-end">ITBIS</th>
              <th style={{ width: 120 }} className="text-end">Total</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
              const lineItbis = lineExento(item) ? 0 : lineTotal * (item.itbisRate ?? 0.18);
              return (
                <tr key={i}>
                  <td className="align-middle text-muted">{i + 1}</td>
                  <td>
                    <SearchSelect
                      options={products}
                      value={item.product}
                      getKey={p => p._id}
                      getLabel={p => p.sku || ''}
                      onSelect={p => handleProductChange(i, p._id)}
                      placeholder="Buscar No. de Item..."
                      emptyLabel="Sin productos coincidentes"
                    />
                  </td>
                  <td>
                    <SearchSelect
                      options={products}
                      value={item.product}
                      getKey={p => p._id}
                      getLabel={p => p.name}
                      onSelect={p => handleProductChange(i, p._id)}
                      placeholder="Buscar producto..."
                      emptyLabel="Sin productos coincidentes"
                    />
                  </td>
                  <td>
                    <Form.Control
                      value={item.description}
                      onChange={e => updateItem(i, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number" min="0" step="1"
                      value={item.quantity}
                      onChange={e => updateItem(i, { quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number" min="0" step="0.01"
                      value={item.unitPrice}
                      onChange={e => updateItem(i, { unitPrice: e.target.value, priceEdited: true })}
                    />
                  </td>
                  <td className="text-end align-middle">{money(lineTotal)}</td>
                  <td className="text-end align-middle">{money(lineItbis)}</td>
                  <td className="text-end align-middle"><strong>{money(lineTotal + lineItbis)}</strong></td>
                  <td className="align-middle">
                    <Button size="sm" variant="outline-danger" onClick={() => removeItem(i)} disabled={items.length === 1}>
                      <i className="bi bi-x" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-secondary" onClick={addItem}>+ Agregar Línea</Button>

        <div className="d-flex justify-content-end mt-4">
          <div style={{ minWidth: 260 }}>
            <div className="d-flex justify-content-end mb-3">
              <Form.Check
                label="Factura exenta de ITBIS"
                checked={invoiceExento}
                onChange={e => setInvoiceExento(e.target.checked)}
              />
            </div>
            <div className="small text-muted mb-3 text-end">Marcarla exime todas las líneas. Los productos exentos (ej.: oxígeno medicinal) nunca llevan ITBIS, aunque esté desmarcada.</div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Subtotal</span>
              <span>{money(totals.subtotal)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">ITBIS</span>
              <span>{money(totals.itbis)}</span>
            </div>
            <hr style={{ borderColor: 'var(--mm-line)' }} />
            <div className="d-flex justify-content-between">
              <span style={{ fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>Total</span>
              <span style={{ fontWeight: 700, fontSize: '1.3rem', color: 'var(--mm-cyan)' }}>{money(grandTotal)}</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="outline-secondary" onClick={() => navigate('/invoices')}>Cancelar</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? 'Guardando...' : (id ? 'Guardar Cambios' : 'Publicar Factura')}
        </Button>
      </div>

      <Modal show={showSendModal} onHide={() => {}} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Factura guardada</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sendEcfError && <div className="alert alert-danger">{sendEcfError}</div>}
          {/* An internal factura (Protocolo 514): no talk of reporting it to DGII here (the user's choice, 2026-10-19) —
              "Enviar a DGII" stays on its row in Facturas. */}
          {savedInternal ? (
            <p className="mb-0">Quedó emitida. Ya puedes imprimirla.</p>
          ) : isAdmin ? (
            <>
              <p className="mb-2">¿Deseas enviar esta factura a DGII ahora?</p>
              <p className="text-muted small mb-0">
                Si eliges "Más tarde", la factura queda en estado "Lista" y puedes enviarla luego desde la lista de
                Facturas con el botón "Enviar a DGII". Recuerda que DGII exige la transmisión dentro de 72 horas.
              </p>
            </>
          ) : (
            <>
              <p className="mb-2">¿Deseas pedir aprobación para enviar esta factura a DGII?</p>
              <p className="text-muted small mb-0">
                Un administrador la revisa y la envía a DGII; <strong>tú no tienes que esperar</strong>. Después de pedirla, la factura aparece
                como "Esperando aprobación" en la lista de Facturas. Si eliges "Más tarde", queda como "Lista" y puedes pedirla luego con
                el botón "Pedir envío a DGII". Recuerda que DGII exige la transmisión dentro de 72 horas.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {savedInternal ? (
            <>
              <Button variant="outline-secondary" disabled={sendingEcf} onClick={() => window.open(`/invoices/${publishedId}/print`, '_blank')}>
                <i className="bi bi-printer" /> Imprimir
              </Button>
              <Button onClick={handleSkipSend} disabled={sendingEcf}>Listo</Button>
            </>
          ) : (
            <>
              <Button variant="outline-secondary" onClick={handleSkipSend} disabled={sendingEcf}>Más tarde</Button>
              <Button onClick={handleConfirmSend} disabled={sendingEcf}>
                {sendingEcf ? (isAdmin ? 'Enviando...' : 'Pidiendo...') : (isAdmin ? 'Sí, enviar a DGII' : 'Sí, pedir aprobación')}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InvoiceBuilder;
